<template>
  <div
    @mouseover="open"
    @mouseleave="close"
    :class="`sidebar separator-border ${expanded ? 'expanded' : ''}`"
    :style="`width: ${sidebarWidth}px`"
  >
    <div class="sidebar-content">
      <b-list-group
        class="sidebar-list separator-border"
        v-for="(group, i) in links"
        :key="`sidebar-link_group_${i}`"
      >
        <div>
          <div class="sidebar-item title-item" v-if="group.title">
            <div class="icon-wrapper">
              <i v-if="group.icon" :class="'bi bi-' + group.icon" />
            </div>
            <span class="title">{{ group.title }}</span>
          </div>
          <b-list-group-item
            v-for="(item, j) in group.items"
            :key="`sidebar-link_${j}`"
            :to="item.to"
            class="p-0 sidebar-item"
            :disabled="item.disabled"
            active-class="current"
          >
            <div class="icon-wrapper">
              <i v-if="item.icon" :class="'bi bi-' + item.icon" />
            </div>
            <span>{{ item.text }}</span>
            <b-badge v-if="item.badge">{{ item.badge }}</b-badge>
          </b-list-group-item>
        </div>
      </b-list-group>
      <div class="footer separator-border p-1">
        <b-img :src="aisonLogo" fluid />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';
import aisonLogo from '@/assets/aison_logo.png';

export default defineComponent({
  name: 'Sidebar',
  components: {},
  data() {
    return {
      expanded: false,
      aisonLogo,
    };
  },
  computed: {
    ...mapGetters({
      totalTasks: 'task/totalCount',
      runningTasks: 'task/runningCount',
    }),
    ...mapState({
      tasksLoading: (state: any) => state.task.loadingList,
    }),
    sidebarWidth(): number {
      return this.expanded ? 240 : 54;
    },
    hasTasks(): boolean {
      return !!this.totalTasks;
    },
    links(): any[] {
      return [
        {
          items: [
            {
              to: '/tasks',
              text: 'Tasks',
              disabled: !this.hasTasks,
              icon: 'list-task',
              badge: `${this.runningTasks} / ${this.totalTasks}`,
            },
          ],
        },
        {
          title: 'Application',
          // icon: 'file-plus',
          items: [
            {
              to: '/fields/connections',
              text: 'Connections',
              icon: 'server',
            },
            {
              to: '/fields/application',
              text: 'Application',
              icon: 'gear',
            },
          ],
        },
        {
          title: 'Migrate',
          // icon: 'box-arrow-right',
          items: [
            {
              to: '/migrate/connections',
              text: 'Connections',
              icon: 'server',
            },
            {
              to: '/migrate/queries',
              text: 'Queries',
              icon: 'list-task',
            },
            {
              to: '/migrate/transform',
              text: 'Transform',
              icon: 'filter',
            },
            {
              to: '/migrate/summary',
              text: 'Summary',
              icon: 'clipboard',
            },
          ],
        },
      ];
    },
  },
  methods: {
    currentRoute(path: string) {
      return this.$route?.path === path;
    },
    open() {
      this.expanded = true;
    },
    close() {
      this.expanded = false;
    },
  },
});
</script>

<style>
.separator-border {
  border-width: 0;
  border-style: solid;
  border-color: var(--separator-line);
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  overflow-y: auto;
  padding-top: 56px;
  transition: width 225ms cubic-bezier(0, 0, 0.2, 1);
  border-right-width: 1px;
  z-index: 100;
  background-color: var(--background-elevated);
}

.sidebar .footer {
  border-top-width: 1px;
  transition: padding 225ms cubic-bezier(0, 0, 0.2, 1);
}

.sidebar .footer img {
  max-width: 85px;
  width: 100%;
  margin: 0 auto;
  display: block;
}

.sidebar .title-item {
  color: var(--text-medium-emphasis);
}

.sidebar .title {
  margin: 0;
  font-size: 0.8rem;
}

.sidebar-list .list-group-item {
  border-width: 0;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  font-weight: 500;
}

.sidebar-list .list-group-item.disabled {
  background-color: unset;
}

.sidebar-list:not(:first-child) {
  border-top-width: 1px;
}

.sidebar-item {
  height: 48px;
  align-items: center;
  display: flex;
  background-color: unset;
}

.sidebar-item .badge {
  margin-left: auto;
  margin-right: 16px;
  font-size: 0.65rem;
  background-color: unset;
  color: inherit;
}

.sidebar .sidebar-item span {
  transition: all 225ms cubic-bezier(0, 0, 0.2, 1);
}

.sidebar:not(.expanded) .sidebar-item span {
  width: 0;
  opacity: 0;
}

.sidebar-item.list-group-item:hover {
  background-color: var(--background-hovered);
}

.sidebar-item.current {
  background-color: var(--background-hovered);
}

.sidebar-item .icon-wrapper {
  min-width: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
